import { describeError } from '@/lib/form/errorMessages';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { Permission, type CatalogItemListItem, type CatalogItemTypeValue } from '@kreart/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import { EmptyState, ErrorState, TableRowSkeleton } from '@/components/feedback';
import { useAuth } from '@/features/auth/useAuth';
import { useCatalogItems, useDeleteCatalogItem } from '@/features/catalog/hooks';
import { toListQuery, type CatalogListSearch } from '@/features/catalog/search';
import { errorKeyFor } from '@/features/catalog/errorMessages';
import { ConfirmDialog } from '@/features/users/components/ConfirmDialog';
import { CatalogTabs } from './CatalogTabs';
import { ColumnOrderMenu } from '@/components/table/ColumnOrderMenu';
import { usePersistedColumnOrder } from '@/components/table/usePersistedColumnOrder';

interface Props {
  type: CatalogItemTypeValue;
  // i18n namespace for the page (e.g. 'cabinets'). Common keys live under
  // `catalog.*`; per-type strings (titles, nav labels, breadcrumbs) under
  // `<i18nNs>.*`.
  i18nNs: string;
  // Routing strings for the parent route. Each per-type page binds its own
  // routes and passes them in.
  listTo:
    | '/cabinets'
    | '/panels'
    | '/countertops'
    | '/drawers'
    | '/elements'
    | '/lighting'
    | '/hardware';
  newTo:
    | '/cabinets/new'
    | '/panels/new'
    | '/countertops/new'
    | '/drawers/new'
    | '/elements/new'
    | '/lighting/new'
    | '/hardware/new';
  editToParent:
    | '/cabinets/$id'
    | '/panels/$id'
    | '/countertops/$id'
    | '/drawers/$id'
    | '/elements/$id'
    | '/lighting/$id'
    | '/hardware/$id';
  extraActions?: ReactNode;
}

interface CatalogTableColumn {
  key: string;
  label: string;
  className?: string;
  render: (item: CatalogItemListItem) => ReactNode;
}

export function CatalogTypePage(props: Props) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as CatalogListSearch;

  const can = (p: string) => user?.permissions.includes(p) ?? false;
  const canCreate = can(Permission.CATALOG_CREATE);
  const canUpdate = can(Permission.CATALOG_UPDATE);
  const canDelete = can(Permission.CATALOG_DELETE);
  const canReadCost = can(Permission.CATALOG_READ_COST);

  const query = useMemo(() => toListQuery(search, props.type), [search, props.type]);
  const { data, isLoading, isError, error, isPlaceholderData, refetch } = useCatalogItems(query);

  const [searchInput, setSearchInput] = useState(search.search ?? '');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => setSearchInput(search.search ?? ''), [search.search]);

  const updateSearch = (patch: Partial<CatalogListSearch>, opts: { replace?: boolean } = {}) => {
    const next: CatalogListSearch = { ...search, ...patch };
    const clean: CatalogListSearch = {};
    if (next.page && next.page !== 1) clean.page = next.page;
    if (next.pageSize && next.pageSize !== 25) clean.pageSize = next.pageSize;
    if (next.search) clean.search = next.search;
    if (next.includeDeleted) clean.includeDeleted = true;
    void navigate({ to: props.listTo, search: clean, replace: opts.replace });
  };

  const onSearchChange = (value: string) => {
    setSearchInput(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const trimmed = value.trim();
      updateSearch({ search: trimmed || undefined, page: 1 }, { replace: true });
    }, 300);
  };

  const [deleteTarget, setDeleteTarget] = useState<CatalogItemListItem | null>(null);
  const deleteMut = useDeleteCatalogItem(deleteTarget?.id ?? '');

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMut.mutateAsync();
      setDeleteTarget(null);
      deleteMut.reset();
    } catch {
      /* surfaced via deleteMut.error */
    }
  };

  const currentPage = search.page ?? 1;
  const pageSize = search.pageSize ?? 25;
  const total = data?.total ?? 0;
  const lastPage = Math.max(1, Math.ceil(total / pageSize));

  const tableColumns: CatalogTableColumn[] = [
    {
      key: 'name',
      label: t('catalog.columns.name'),
      render: (c) => {
        const isDeleted = c.deletedAt !== null;
        return (
          <div className="flex items-center gap-2">
            <span className="font-medium">{c.name}</span>
            {isDeleted && (
              <span className="rounded-md bg-destructive/10 px-2 py-0.5 text-xs text-destructive">
                {t('catalog.badges.deleted')}
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: 'sku',
      label: t('catalog.columns.sku'),
      className: 'text-muted-foreground',
      render: (c) => c.sku ?? '—',
    },
    {
      key: 'dimensions',
      label: t('catalog.columns.dimensions'),
      className: 'text-muted-foreground',
      render: (c) => formatDims(c),
    },
    {
      key: 'material',
      label: t('catalog.columns.material'),
      className: 'text-muted-foreground',
      render: (c) => c.material ?? '—',
    },
    ...(canReadCost
      ? [
          {
            key: 'cost',
            label: t('catalog.columns.cost'),
            className: 'text-right tabular-nums text-muted-foreground',
            render: (c: CatalogItemListItem) =>
              c.defaultCostNet ? `${c.defaultCostNet} ${c.currency}` : '—',
          },
        ]
      : []),
    {
      key: 'actions',
      label: t('catalog.columns.actions'),
      className: 'text-right',
      render: (c) => {
        const isDeleted = c.deletedAt !== null;
        return (
          <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
            {!isDeleted && (
              <Link to={props.editToParent} params={{ id: c.id }}>
                <Button variant="ghost" aria-label={t('catalog.actions.open') ?? 'open'}>
                  <Pencil className="h-4 w-4" />
                </Button>
              </Link>
            )}
            {canDelete && !isDeleted && (
              <Button
                variant="ghost"
                onClick={() => setDeleteTarget(c)}
                aria-label={t('common.delete') ?? 'delete'}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        );
      },
    },
  ];
  const { orderedColumns, moveColumn, resetColumnOrder } = usePersistedColumnOrder(
    `catalog.${props.type}`,
    tableColumns,
  );
  const colCount = orderedColumns.length;

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-semibold">{t('catalog.librarieTitle')}</h1>
        <div className="flex flex-col gap-2 sm:flex-row">
          {props.extraActions}
          {canCreate && (
            <Link to={props.newTo}>
              <Button className="w-full sm:w-auto">
                <Plus className="mr-1.5 h-4 w-4" />
                {t(`${props.i18nNs}.new`)}
              </Button>
            </Link>
          )}
        </div>
      </div>

      <CatalogTabs />

      <div className="relative">
        <Input
          value={searchInput}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={t('catalog.filters.searchPlaceholder') ?? ''}
        />
      </div>

      {isError && <ErrorState error={error} onRetry={() => void refetch()} />}
      {deleteMut.isError && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
          {describeError(t, deleteMut.error, errorKeyFor)}
        </div>
      )}

      <div
        className={
          'overflow-x-auto rounded-lg border border-border bg-card ' +
          (isPlaceholderData ? 'opacity-70 transition-opacity' : '')
        }
      >
        <div className="mb-2 flex justify-end">
          <ColumnOrderMenu
            columns={orderedColumns}
            onMoveColumn={moveColumn}
            onReset={resetColumnOrder}
          />
        </div>
        <Table>
          <THead>
            <TR>
              {orderedColumns.map((column) => (
                <TH key={column.key} className={column.className}>
                  {column.label}
                </TH>
              ))}
            </TR>
          </THead>
          {isLoading ? <TableRowSkeleton columns={colCount} /> : null}
          <TBody>
            {!isLoading && data && data.items.length === 0 && (
              <TR>
                <TD colSpan={colCount} className="py-6">
                  <EmptyState
                    description={t(`${props.i18nNs}.empty`)}
                    action={
                      canCreate ? (
                        <Link to={props.newTo}>
                          <Button className="h-8 px-3 py-1 text-xs">
                            <Plus className="mr-1.5 h-4 w-4" />
                            {t(`${props.i18nNs}.new`)}
                          </Button>
                        </Link>
                      ) : undefined
                    }
                  />
                </TD>
              </TR>
            )}
            {data?.items.map((c) => {
              const isDeleted = c.deletedAt !== null;
              const editable = !isDeleted;
              return (
                <TR
                  key={c.id}
                  onClick={() =>
                    editable && navigate({ to: props.editToParent, params: { id: c.id } })
                  }
                  className={editable ? 'cursor-pointer hover:bg-muted/30 transition-colors' : ''}
                >
                  {orderedColumns.map((column) => (
                    <TD key={column.key} className={column.className}>
                      {column.render(c)}
                    </TD>
                  ))}
                </TR>
              );
            })}
          </TBody>
        </Table>
      </div>

      <div className="flex flex-col gap-2 text-sm text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
        <div>
          {t('catalog.pagination.range', {
            from: total === 0 ? 0 : (currentPage - 1) * pageSize + 1,
            to: Math.min(currentPage * pageSize, total),
            total,
          })}
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            disabled={currentPage <= 1}
            onClick={() => updateSearch({ page: currentPage - 1 })}
          >
            {t('catalog.pagination.prev')}
          </Button>
          <Button
            variant="outline"
            disabled={currentPage >= lastPage}
            onClick={() => updateSearch({ page: currentPage + 1 })}
          >
            {t('catalog.pagination.next')}
          </Button>
        </div>
      </div>

      <ConfirmDialog
        open={deleteTarget !== null}
        title={t('catalog.danger.confirmTitle')}
        description={t('catalog.danger.confirmDescription', { name: deleteTarget?.name ?? '' })}
        confirmLabel={t('catalog.danger.deleteButton')}
        variant="destructive"
        loading={deleteMut.isPending}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
      />
      {/* Touch the canUpdate flag so the lint rule doesn't complain in PRs that
          don't currently expose an inline edit affordance. The edit page itself
          guards by permission. */}
      <span data-can-update={canUpdate ? 'true' : 'false'} className="hidden" />
    </div>
  );
}

function formatDims(c: CatalogItemListItem): string {
  if (c.type === 'HARDWARE' || c.type === 'LIGHTING') {
    return '—';
  }
  if (c.type === 'COUNTERTOP') {
    const parts = [c.defaultWidthMm, c.defaultDepthMm, c.thicknessMm];
    if (parts.every((p) => p === null)) return '—';
    return parts.map((p) => (p === null ? '?' : String(p))).join(' × ') + ' mm';
  }
  if (c.type === 'PANEL') {
    const parts = [c.defaultHeightMm, c.defaultWidthMm, c.thicknessMm];
    if (parts.every((p) => p === null)) return '—';
    return parts.map((p) => (p === null ? '?' : String(p))).join(' × ') + ' mm';
  }
  const parts = [c.defaultWidthMm, c.defaultHeightMm, c.defaultDepthMm];
  if (parts.every((p) => p === null)) return '—';
  return parts.map((p) => (p === null ? '?' : String(p))).join(' × ') + ' mm';
}
